import {
	ChangeDetectionStrategy,
	Component,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BaseComponent } from '@core/base.component';
import { ICloserDefaults } from '@models/applicationDefaults';
import { Select } from '@ngxs/store';
import { ApplicationDefaultsRepo } from '@repos/application-defaults/application-defaults.repo';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { MenusService } from '@services/menus/menus.service';
import { NavigationService } from '@services/nav/nav.service';
import { AppDispatch } from '@state/app/app.dispatch';
import { SideNavFlyoutType } from '@state/app/app.model';
import { NavigationState } from '@state/navigation/navigation.state';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { BUTTON_PRESETS } from 'src/app/ui/button/button.component';
import { Menu } from '../../../ui/menu/menu.component';
import { CreateMatterModalComponent } from '../../create-matter/modal/modal.component';
import { LeapCreateMatterComponent } from '../global-side-nav/leap-create-matter/leap-create-matter.component';
import { NavbarModule } from './navbar.module';
import { NavbarSelectors, NavbarState } from './navbar.selector';
import { TestHooks } from './navbar.hooks';
import { THEMES } from 'src/app/ui/utils/constants';

interface VM {
	mainMenu: Menu;
	inboxMenu: Menu;
	settingsMenu: Menu;
	flyoutState: SideNavFlyoutType;
	loadedFromLeap: boolean;
	showSideNav: boolean;
	specialOccasionLogoUrl?: string;
}

const defaultVM: VM = {
	mainMenu: {
		items: [],
	},
	inboxMenu: {
		items: [],
	},
	settingsMenu: {
		items: [],
	},
	flyoutState: null,
	loadedFromLeap: false,
	showSideNav: false,
	specialOccasionLogoUrl: null,
};

@Component({
	selector: 'app-navbar',
	imports: [NavbarModule],
	styles: [
		`
			:host {
				@apply h-full;
			}
		`,
	],
	templateUrl: './navbar.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent
	extends BaseComponent<VM>
	implements OnInit, OnDestroy
{
	testHooks = TestHooks;
	@Select(NavbarSelectors.getNavbarState) navbarState$: Observable<NavbarState>;
	@Select(NavigationState.currentUrl) currentUrl$: Observable<string>;
	private navbarStateSub: Subscription;
	private currentUrlSub: Subscription;
	private logoUrlSub: Subscription;
	public SideNavFlyoutType = SideNavFlyoutType;
	public preset = BUTTON_PRESETS;
	public THEMES = THEMES;

	constructor(
		private navService: NavigationService,
		private menusService: MenusService,
		private appDispatch: AppDispatch,
		private dialog: MatDialog,
		private analytics: AnalyticsService,
		private applicationDefaultsRepo: ApplicationDefaultsRepo,
	) {
		super(defaultVM);
	}

	ngOnInit(): void {
		this.navbarStateSub = this.navbarState$.subscribe((navbarState) => {
			this.VM = {
				flyoutState: navbarState.sideNavFlyOut,
				loadedFromLeap: navbarState.loadedFromLeap,
				showSideNav: navbarState.showSideNav,
				settingsMenu: this.menusService.getSettingsMenu(),
			};

			switch (navbarState.sideNavFlyOut) {
				case SideNavFlyoutType.SEARCH:
					this.clearActiveNonRouteItems();
					this.VM = {
						mainMenu: {
							...this.VM.mainMenu,
							items: this.VM.mainMenu.items.map((item) => {
								if (item.title.label === 'Search') {
									item.active = true;
								}
								return item;
							}),
						},
					};
					break;
				case SideNavFlyoutType.APPOINTMENTS:
					this.clearActiveNonRouteItems();
					this.VM = {
						mainMenu: {
							...this.VM.mainMenu,
							items: this.VM.mainMenu.items.map((item) => {
								if (item.title.label === 'Appointments') {
									item.active = true;
								}
								return item;
							}),
						},
					};
					break;
				case SideNavFlyoutType.SUPPORT:
					this.clearActiveNonRouteItems();
					this.VM = {
						settingsMenu: {
							...this.VM.settingsMenu,
							items: this.VM.settingsMenu.items.map((item) => {
								if (item.title.label === 'Support') {
									item.active = true;
								}
								return item;
							}),
						},
					};
					break;
				default:
					this.clearActiveNonRouteItems();
					break;
			}
		});

		this.VM = {
			mainMenu: this.menusService.getMainMenu(),
			inboxMenu: this.menusService.getInboxMenu(),
		};

		this.currentUrlSub = this.currentUrl$
			.pipe(distinctUntilChanged())
			.subscribe(() => {
				if (this.VM.flyoutState) this.closeSideNav();
			});

		this.logoUrlSub = this.applicationDefaultsRepo
			.GetCloserDefaults()
			.subscribe((defaults) => {
				this.VM = {
					specialOccasionLogoUrl: (defaults as ICloserDefaults).logo?.imageUrl,
				};
			});
	}

	ngOnDestroy(): void {
		this.navbarStateSub.unsubscribe();
		this.currentUrlSub.unsubscribe();
		this.logoUrlSub.unsubscribe();
	}

	goHome() {
		this.navService.goToDashboard();
	}

	closeSideNav() {
		this.appDispatch.CloseSideNav();
	}

	addFile(): void {
		if (this.VM.loadedFromLeap) {
			this.dialog.open(LeapCreateMatterComponent, {
				maxWidth: '1000px',
				width: '100%',
				panelClass: 'no-padding',
			});
		} else {
			this.dialog.open(CreateMatterModalComponent, {
				maxWidth: '1000px',
				width: '100%',
				panelClass: 'no-padding',
			});
		}
		this.close();
	}

	close(): void {
		this.analytics.logEvent(
			AnalyticsEvent.CLOSER_BUTTON_NAVIGATE_TO_DASHBOARD_CLICK,
		);
		this.appDispatch.CloseSideNav();
	}

	clearActiveNonRouteItems() {
		this.VM = {
			mainMenu: {
				...this.VM.mainMenu,
				items: this.VM.mainMenu.items.map((item) => {
					if (
						item.title.label === 'Search' ||
						item.title.label === 'Appointments'
					) {
						item.active = false;
					}
					return item;
				}),
			},
			settingsMenu: {
				...this.VM.settingsMenu,
				items: this.VM.settingsMenu.items.map((item) => {
					if (item.title.label === 'Support') {
						item.active = false;
					}
					return item;
				}),
			},
		};
	}
}
